<template>
  <section class="reference">
    <nav class="reference-navigation">
      <ul v-scroll-spy-active v-scroll-spy-link>
        <li v-for="n in 7" :key="n">
          <a :href="'#reference-pane-' + n"></a>
        </li>
      </ul>
    </nav>

    <div v-scroll-spy>
      <figure class="reference-pane" id="reference-pane-1">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <h2 class="reference-page-title">How it Works</h2>
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <p>Cotton Bureau is kind of like a cool mall. Remember those? Lots of indie shops to browse plus a few big stores everyone knows.</p>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      If you want to get in on the fun, we’d love to help. Every day the world’s most incredible designers and illustrators, causes and charities, and creative communities (podcasters,
                      app and game developers, design agencies, blogs and media outlets, etc.) turn their ideas into real products.
                    </p>
                    <p>Since 2010 we’ve shipped over a million products. Is yours next?</p>
                    <div class="reference-pane-actions">
                      <InertiaLink class="reference-pane-action-link" href="/about-us">
                        Read More About Us
                        <NylonIcon icon-name="uiArrow--Right" />
                      </InertiaLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-2">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <h3 class="reference-pane-heading reference-pane-heading--full-border">Yes, But how does Cotton Bureau work?</h3>
                  <div class="reference-pane-graphic">
                    <img src="https://s3.amazonaws.com/cottonbureau.com/img/how-it-works/how_it_works.svg" loading="lazy" />
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <p>We’re here for you every step of the way.</p>
                  </div>
                  <div class="reference-pane-text">
                    <p>
                      Whether you’re looking for the convenience of on demand, the quality of screen printing, or the flexibility of our stock and pre-order campaign models, we’re your one-stop
                      ecommerce shop.
                    </p>
                    <p>
                      From creating an account to getting your first product live, Cotton Bureau is completely self-serve. Once your account has been approved by our t-shirt task force, you’re ready
                      to start selling.
                    </p>
                    <p>
                      If you need a little help though, operators are standing by to make sure your artwork is print-ready (and provide light creative direction if necessary). We’re also more than
                      happy to discuss recommended prices, help choose shirt colors, fabrics, and ink types, review your account and profile, and more. Don’t be afraid to
                      <a href="mailto:us@cottonbureau.com">reach out</a>
                      .
                    </p>
                    <p>
                      Just like that, your product is live. Maybe the clock starts ticking on a two week pre-order campaign, or maybe you chose to use our stock or on demand options. No matter which
                      sales method you use, you can count on Cotton Bureau to handle everything from production to taxes to replacements and exchanges.
                    </p>
                    <p>
                      When it’s time to start production, you can rely on our tested network of local, national, and international partners. We’ve been using the same trusted team for the last 10+
                      years to print, embroider, cast, forge, decorate, and otherwise manufacture exactly what you need and when you need it.
                    </p>
                    <div class="reference-pane-actions">
                      <InertiaLink class="reference-pane-action-link" href="/create-a-product">
                        See For Yourself
                        <NylonIcon icon-name="uiArrow--Right" />
                      </InertiaLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-3">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <h3 class="reference-pane-heading reference-pane-heading--full-border">
              <span class="reference-pane-heading-wrapper">Why should I work with Cotton Bureau instead of some other print-me-a-shirt service?</span>
            </h3>
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <ul>
                      <li>No AI slop</li>
                      <li>Only the best designs</li>
                      <li>The absolute finest-quality shirts and apparel</li>
                      <li>Dependable printing and production</li>
                      <li>Slick online shopping experience</li>
                      <li>Attentive customer service</li>
                      <li>Did we mention real people? No bots allowed. 🤖</li>
                    </ul>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      Listen, there are a zillion places on the internet to get a crappy t-shirt made, but we’re not one of them. We’ve made a name for ourselves by focusing on quality at every turn:
                      the best designers and communities, comfy and stylish tees, a rock-solid print shop, a slick and accessible website experience, and caring and swift customer service.
                    </p>
                    <p>
                      If we team up on a t-shirt together, you can rest assured that it won’t be for sale next to someone’s charity 5K tee, printed on low quality scratchy tees with the wrong color
                      ink, and shipped three weeks late by people you can’t reach via email. We’ve been doing this since 2013, and we’re getting better all the time. We got this.
                    </p>
                    <p>
                      Oh, by the way, all customer service is handled 100% in-house by real people — no ifs, ands, or bots. Refunds, exchanges, lost packages? Just
                      <a href="mailto:us@cottonbureau.com">email us@cottonbureau.com</a>
                      . We’re here to help.
                    </p>
                    <div class="reference-pane-actions">
                      <InertiaLink class="reference-pane-action-link" href="/create-a-product">
                        Join the Club
                        <NylonIcon icon-name="uiArrow--Right" />
                      </InertiaLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-4">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <h3 class="reference-pane-heading reference-pane-heading--full-border">Couldn’t I do it myself?</h3>
                    <p>
                      Sure. We have fond memories of doing it ourselves, too. Once upon a time, before Cotton Bureau was even a twinkle in our eye, we set up a little internet t-shirt shop of our own.
                      If designing and building a store is your kind of fun, we salute you.
                    </p>
                    <p>
                      For everyone else who just wants to be up and running in minutes, that’s why Cotton Bureau exists. No need to set shipping rates or mess around with tariff codes. No 3PLs to
                      investigate, international laws to parse, or packages to track down. Say sayonara to sales tax. Enjoy global shipping at competitive rates. Sit back and relax while someone else
                      handles the customer service.
                    </p>
                    <p>Sound too good to be true? There’s only one way to find out.</p>
                    <div class="reference-pane-actions">
                      <InertiaLink class="reference-pane-action-link" href="/create-a-product">
                        Get Started Now
                        <NylonIcon icon-name="uiArrow--Right" />
                      </InertiaLink>
                    </div>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-graphic">
                    <img src="https://s3.amazonaws.com/cottonbureau.com/img/how-it-works/how_it_works_factory.svg" loading="lazy" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-5">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <h3 class="reference-pane-heading reference-pane-heading--full-border">I’m sold. What do I have to do?</h3>
                  <p>&nbsp;</p>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <p>We thought you’d never ask.</p>
                  </div>
                  <div class="reference-pane-text">
                    <p>
                      All you need to get started is a design. Simply grab your artwork, choose the product type, and add a title and description. Once you create your first product, we’ll take a look
                      to make sure it’s a good fit for our audience. While we review your work, go ahead and fill out your profile — including payment information. It only takes a minute. Before you
                      know it, your shirt is live and it’s time to promote, so take to the highways and byways to let the people know: you’re in business!
                    </p>
                    <p style="font-style: italic">
                      P.S. The standards are high to be included in the official Cotton Bureau shop. If know you’re not quite up to snuff, but you still need someone to help print and ship shirts for
                      your school chess club, organic meal delivery startup, family choir, or cross-country road trip, check out
                      <InertiaLink href="/custom">Cotton Bureau Custom</InertiaLink>
                      . It’s the same quality and service you love minus the smug designer spectacles.
                    </p>
                    <div class="reference-pane-actions">
                      <InertiaLink class="reference-pane-action-link" href="/create-a-product">
                        Create Your First Product
                        <NylonIcon icon-name="uiArrow--Right" />
                      </InertiaLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-6">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <h3 class="reference-pane-heading">How much money will I make?</h3>
            <div class="reference-pane-container reference-pane-chart-container">
              <div class="reference-pane-chart">
                <img src="https://s3.amazonaws.com/cottonbureau.com/img/how-it-works/how_it_works_chart.svg" loading="lazy" />
              </div>
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-lede reference-pane-lede-small">
                    <p>Whether you just want to make a few extra bucks, raise money for a worthy cause, or generate actual revenue for your business, Cotton Bureau can help.</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      There’s literally no limit to how much money you can make when you work with Cotton Bureau. Will you see seven figures in profit? Probably not, but if you do, it wouldn’t be our
                      first time.
                    </p>
                    <p>Either way, it’s 100% free to get started. All approved accounts have access to our complete on demand catalog and extensive pre-order campaign services.</p>
                    <p>When you’re ready, just pick a product, add your profit margin, and tell people about it. It’s that simple.</p>
                    <p>Once a month as long as you’ve earned at least $25 Cotton Bureau sends you money via PayPal or direct bank transfer.</p>
                    <p>Not a bad deal for a couple of minutes setting up an account and telling a few friends.</p>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      Every time you sell an on demand shirt, hat, phone case, tote, tumbler, etc. you make money. If you sell enough, we start handing out bonuses like candy. First an extra $1 for
                      each sale, then an extra $2, and it doesn’t stop there. The more you sell, the better for us all, you know?
                    </p>
                    <p>
                      If you decided to run a pre-order campaigns, it works the same way, except there’s a 12 shirt minimum before anything gets made (and you get paid). The bar to clear is a little
                      higher than on demand, but the payoff is a shirt that will last a lifetime.
                    </p>
                    <div class="reference-pane-actions">
                      <InertiaLink class="reference-pane-action-link" href="/create-a-product">
                        Let’s Get Started
                        <NylonIcon icon-name="uiArrow--Right" />
                      </InertiaLink>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-7">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <h3 class="reference-pane-heading">What else can we do together?</h3>
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <p>Lots of things, it turns out.</p>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      Our selection of on demand and pre-order products is substantial (and growing), but sometimes you need help with something a little more custom like enamel pins, challenge coins,
                      or embroidered patches. We can assist with those as well. If that’s you,
                      <a href="mailto:sellers@cottonbureau.com">email us</a>
                      about joining Cotton Bureau Pro.
                    </p>
                    <p>
                      All upgraded accounts are eligible for our stock products program and receive an instant $1 bonus on all future on demand and pre-order campaign sales. Cotton Bureau Pro is just
                      $9 / month which includes a $5 monthly storage credit.
                    </p>
                  </div>
                  <div class="reference-pane-text">
                    <p>
                      For those looking for a more immersive customer experience, we also offer Cotton Bureau Plus. Integrate your brand’s colors, fonts, and styles directly into your storefront and
                      product pages. Cotton Bureau Plus is $29 / month plus a one time store setup fee of $6,000.
                    </p>
                    <p>
                      All Plus accounts are immediately upgraded to a Tier 3 account which includes a +$2 bonus for all on demand and campaign sales. All stock product setup fees are waived for the
                      first round of products. Plus accounts also include $19 in monthly storage credits, dedicated VIP support, and access to our vast network of exceptional designers and
                      illustrators.
                    </p>
                    <p>
                      If you’re looking for the right partner to take a more hands-on approach, consider joining
                      <InertiaLink href="/people/letterboxd">Letterboxd</InertiaLink>
                      ,
                      <InertiaLink href="/people/texas-monthly">Texas Monthly</InertiaLink>
                      ,
                      <InertiaLink href="/people/font-awesome">Font Awesome</InertiaLink>
                      ,
                      <InertiaLink href="/people/obsidian">Obsidian</InertiaLink>
                      , and more.
                    </p>
                    <p>
                      To get started with Cotton Bureau Plus, email
                      <a href="mailto:plus@cottonbureau.com">plus@cottonbureau.com</a>
                      or visit our
                      <InertiaLink href="/plus">Plus gallery and information page</InertiaLink>
                      .
                    </p>
                  </div>
                  <div class="reference-pane-actions">
                    <a class="reference-pane-action-link" href="https://s3.amazonaws.com/cottonbureau.com/resources/Cotton+Bureau+Prices+and+Services.pdf">
                      Browse our complete list of products and services
                      <NylonIcon icon-name="uiArrow--Right" />
                    </a>
                    <a class="reference-pane-action-link" href="https://s3.amazonaws.com/cottonbureau.com/resources/Cotton+Bureau+Product+Catalog.pdf">
                      Download our full product catalog
                      <NylonIcon icon-name="uiArrow--Right" />
                    </a>
                    <a class="reference-pane-action-link" href="mailto:us@cottonbureau.com">
                      Still Have Questions? Email Us
                      <NylonIcon icon-name="uiArrow--Right" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>
    </div>

    <InertiaLink class="hiw-submit" href="/create-a-product">
      <span class="u-hideOnMobile">You’ve seen enough, t</span><span class="u-hideAboveMobile">T</span>ime to create a product. <NylonIcon icon-name="uiArrow--Right" />
    </InertiaLink>
  </section>
</template>

<script>
import { Link as InertiaLink } from "@inertiajs/vue3";
import StorefrontLayout from "@/layout/storefront-layout.vue";
import "@styles/sell/how-it-works.scss";

export default {
  name: "HowItWorks",
  layout: StorefrontLayout,
  components: { InertiaLink },
};
</script>
